import { QueryClient, useQueryClient } from 'react-query';

import { useStateContext } from '../contexts/ContextProvider';
import { ApiError, ApiResponse, useApiMutation, useApiQuery } from './useApi';

export type ProspectingReplyCategory = 'interested' | 'not_now' | 'not_interested' | 'wrong_contact' | 'out_of_office' | 'unsubscribe';
/** Catégories acceptées par « Classer » : la désinscription a sa propre action. */
export type ClassifiableReplyCategory = Exclude<ProspectingReplyCategory, 'unsubscribe'>;
export type ProspectingRepliesFilter = 'open' | 'validated' | 'dismissed';

export type ProspectingReply = {
    id: number;
    status: 'to_classify' | 'classified' | 'validated' | 'dismissed';
    kind: 'reply' | 'auto_reply' | 'unsubscribe_request';
    category: ProspectingReplyCategory | null;
    ai_summary: string | null;
    ai_confidence: number | null;
    ai_return_date: string | null;
    ai_suggestion: Record<string, string | null> | null;
    injection_suspected: boolean;
    from_email: string;
    subject: string | null;
    body_excerpt: string | null;
    received_at: string;
    priority: boolean;
    event_name?: string | null;
    period: { key: string; name: string } | null;
    hotel: { id: number; name: string; city: string | null } | null;
    target: { id: number; status: string; current_step: number; email: string } | null;
    crm_prospect_id: number | null;
    can_resume: boolean;
};

export type ProspectingReplyCounts = { open: number; validated: number; dismissed: number; priority_open: number };

const REPLIES = '/crm/prospecting/replies';

const invalidateReplies = (queryClient: QueryClient) =>
    queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(REPLIES) });

/** `campaignId` : réponses des cibles de cette campagne seulement. */
export function useProspectingReplies(filter: ProspectingRepliesFilter, page = 1, campaignId?: number) {
    return useApiQuery<ApiResponse<ProspectingReply[]>>(
        `${REPLIES}?status=${filter}&page=${page}${campaignId ? `&campaign_id=${campaignId}` : ''}`,
        {},
        { refetchInterval: 60000, keepPreviousData: true },
    );
}

export function useProspectingReplyCounts() {
    return useApiQuery<ProspectingReplyCounts>(`${REPLIES}/counts`, {}, { refetchInterval: 60000 });
}

/** Liste et compteurs sont rafraîchis ; le message de l'API (409, 422) est montré tel quel. */
function useReplyAction<TVars extends { replyId: number }>(action: string) {
    const queryClient = useQueryClient();
    const { setNotifications } = useStateContext();

    return useApiMutation<unknown, ApiError, TVars>('post', (vars) => `${REPLIES}/${vars.replyId}/${action}`, {
        onSuccess: () => invalidateReplies(queryClient),
        onError: (error) => setNotifications({ error: error.response?.data?.message ?? 'Action impossible, réessayez.' }),
    });
}

/** Validation 1 clic : fiche, contact, tâche « rappeler sous 24 h », fil versé. */
export const useValidateProspectingReply = () => useReplyAction<{ replyId: number; category?: 'interested' | 'not_now' }>('validate');
export const useClassifyProspectingReply = () => useReplyAction<{ replyId: number; category: ClassifiableReplyCategory; activate_reserve?: boolean }>('classify');
export const useResumeProspectingSequence = () => useReplyAction<{ replyId: number }>('resume');
export const useUnsubscribeProspectingReply = () => useReplyAction<{ replyId: number }>('unsubscribe');

// File « RDV à rattacher » : RDV lemcal qu'aucune cible ni fiche n'a reconnus.
export type LemcalMeetingToAttach = {
    id: number;
    lemcal_meeting_id: string;
    lead_email: string | null;
    lead_name: string | null;
    start_at: string | null;
    status: 'to_attach' | 'attached' | 'ignored' | 'canceled';
    crm_prospect_id: number | null;
};

const LEMCAL = '/crm/prospecting/lemcal-meetings';

export function useLemcalMeetingsToAttach(page = 1) {
    return useApiQuery<ApiResponse<LemcalMeetingToAttach[]>>(
        `${LEMCAL}?status=to_attach&page=${page}`,
        {},
        { refetchInterval: 60000, keepPreviousData: true },
    );
}

export function useLemcalMeetingCounts(enabled = true, refetchInterval: number | false = 60000) {
    // `enabled` : API gardée par `crm:prospects`, voir `staffQueues.ts`.
    return useApiQuery<{ to_attach: number }>(`${LEMCAL}/counts`, {}, { enabled, refetchInterval });
}

/** File et compteur rafraîchis ; le message de l'API (409 déjà traité ou hôtel client, 422) est montré tel quel. */
function useLemcalAction<TVars extends { meetingId: number }>(action: string) {
    const queryClient = useQueryClient();
    const { setNotifications } = useStateContext();

    return useApiMutation<{ prospect_id?: number }, ApiError, TVars>('post', (vars) => `${LEMCAL}/${vars.meetingId}/${action}`, {
        onSuccess: () => queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(LEMCAL) }),
        onError: (error) => {
            setNotifications({ error: error.response?.data?.message ?? 'Action impossible, réessayez.' });
            // Un 409 signale un RDV traité ailleurs : la file est relue pour le faire disparaître.
            queryClient.invalidateQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(LEMCAL) });
        },
    });
}

export const useAttachLemcalMeeting = () => useLemcalAction<{ meetingId: number; prospect_id: number }>('attach');
export const useCreateProspectFromLemcalMeeting = () => useLemcalAction<{ meetingId: number; name: string }>('create-prospect');
export const useIgnoreLemcalMeeting = () => useLemcalAction<{ meetingId: number }>('ignore');
